import { ArrowRightOnRectangleIcon as LoginIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import type { FormEventHandler } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import type { InferType } from 'yup';

import { allFieldsDirty, setFormErrors } from 'utils/formHelpers';

import { loginFormSchema as schema } from 'validators/loginForm';

import { useLoginMutation } from 'reduxLocal/api/authenticationApi';

import { setAxiosAuthToken } from 'api/utils';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FormError } from '@atoms/FormError/FormError';

import { InputField } from 'components/ui/molecules/InputField';
import { PasswordField } from 'components/ui/molecules/PasswordField';

interface LoginFormProps {
  onLoginSuccess: () => Promise<void>;
  loginExpired: boolean;
}

interface Inputs extends InferType<typeof schema> {
  base: null;
}

const styledLoginIcon = (
  <LoginIcon
    width="40"
    height="40"
    className="size-10 self-center stroke-content-primary stroke-1.5"
  />
);

export const LoginForm = ({ onLoginSuccess, loginExpired }: LoginFormProps) => {
  const locationState = useLocation().state as { mfaExpired: boolean };
  const isExpired = locationState?.mfaExpired;
  const [login, { isLoading }] = useLoginMutation();

  const { register, handleSubmit, setError, formState, watch } =
    useForm<Inputs>({
      mode: 'onSubmit',
      resolver: yupResolver(schema)
    });

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    try {
      await handleSubmit(async (data) => {
        const res = await login(data).unwrap();
        setAxiosAuthToken(res.access_token);

        try {
          await onLoginSuccess();
        } catch (mfaError) {
          console.error('MFA Configuration Error:', mfaError);
          setFormErrors({
            error: { data: 'Failed fetch MFA configuration' },
            setError,
            fields: Object.keys(schema.fields)
          });
        }
      })(event);
    } catch (error) {
      setFormErrors({ error, setError, fields: Object.keys(schema.fields) });
    }
  };

  const email = watch('email');
  const password = watch('password');

  return (
    <>
      {loginExpired ? (
        <div className="align-center mb-5 flex w-full flex-col justify-center">
          {styledLoginIcon}
          <h1 className="mt-5 text-center text-[2rem] font-medium">
            Your login expired
          </h1>
        </div>
      ) : (
        <div className="align-center mb-10 flex w-full justify-center">
          {styledLoginIcon}
          <h1 className="ml-5 text-center text-[2rem] font-medium">Log in</h1>
        </div>
      )}
      {loginExpired && (
        <p className="mb-8 text-center tracking-tight">Please log in again.</p>
      )}
      <form onSubmit={onSubmit}>
        <InputField
          {...register('email')}
          className="mb-10"
          label="Email"
          name="email"
          autoComplete="username"
          errorMessage={formState.errors.email?.message}
        />
        <PasswordField
          {...register('password')}
          className="mb-10"
          label="Password"
          name="password"
          autoComplete="current-password"
          errorMessage={formState.errors.password?.message}
        />
        <PrimaryButton
          className="w-full font-medium"
          htmlType="submit"
          loading={isLoading}
          size="large"
          disabled={!email || !password}
        >
          Log in
        </PrimaryButton>
      </form>
      <FormError message={formState.errors.base?.message} />
      {isExpired && (
        <FormError message="Your token was expired, please login again" />
      )}
    </>
  );
};
